import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShoppingCart, ArrowRight, Download } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { mediaUrl } from "@/lib/media";
import { Section } from "@/components/Section";
import WhatsAppInquireButton from "@/components/WhatsAppInquireButton";

export default function Spares() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/spares-meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (brand) params.brand = brand;
      if (category) params.category = category;
      api.get("/spares", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, brand, category]);

  return (
    <>
      <section className="relative bg-[#1A1A1A] text-white py-20 noise-overlay overflow-hidden" data-testid="spares-hero">
        {settings?.spares_hero_image && (
          <>
            <img src={mediaUrl(settings.spares_hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover object-center opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/30" />
          </>
        )}
        <Section className="relative">
          <div>
            <div className="label-eyebrow mb-4">Spares Inventory</div>
            <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">GENUINE <span className="text-[#E65C00]">SPARES</span></h1>
            <p className="mt-4 text-gray-400 max-w-xl">OEM-grade parts for every brand we service. Place an order in 60 seconds — our team confirms pricing & dispatch within 2 working hours.</p>
            {settings?.spares_catalog_url && (
              <a
                href={mediaUrl(settings.spares_catalog_url)}
                target="_blank"
                rel="noreferrer"
                download
                className="mt-6 inline-flex items-center gap-2 bg-[#FFCC00] text-[#1A1A1A] font-heading font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#E65C00] hover:text-white transition-colors"
                data-testid="spares-catalog-download"
              >
                <Download className="w-4 h-4" /> Download Spares Catalog (PDF)
              </a>
            )}
          </div>
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="space-y-6 lg:sticky lg:top-28 h-fit" data-testid="spares-filters">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Search</label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part name or number" className="w-full pl-10 pr-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" data-testid="spares-search" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Brand</label>
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className="mt-2 w-full px-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm">
              <option value="">All Brands</option>
              {meta.brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full px-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm">
              <option value="">All Categories</option>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </aside>

        <div>
          <div className="mb-6 text-sm text-gray-600">Showing <strong>{items.length}</strong> {items.length === 1 ? "spare" : "spares"}</div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({length:6}).map((_,i)=><div key={i} className="skeleton aspect-[4/5]" />)}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-gray-300 text-gray-500">No spares match. Try removing filters.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((s) => {
                const discounted = s.discount_percent ? s.price * (1 - s.discount_percent / 100) : s.price;
                return (
                  <Link to={`/spares/${s.slug}`} key={s.id} className="card-industrial block group" data-testid={`spare-card-${s.id}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
                      <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {s.discount_percent > 0 && (
                        <div className="absolute top-3 left-3 bg-[#E65C00] text-white px-2 py-1 text-[10px] font-black uppercase tracking-widest">{s.discount_percent}% OFF</div>
                      )}
                      {s.stock_qty === 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><div className="text-white font-heading font-black uppercase">OUT OF STOCK</div></div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{s.brand} · {s.category}</div>
                      <h3 className="font-heading font-black text-base mt-1 leading-tight group-hover:text-[#E65C00] line-clamp-2">{s.title}</h3>
                      {s.part_number && <div className="text-[10px] text-gray-500 font-mono mt-1">P/N: {s.part_number}</div>}
                      <div className="mt-3 flex items-baseline gap-2">
                        <div className="font-heading font-black text-lg">{formatINR(discounted)}</div>
                        {s.discount_percent > 0 && <div className="text-xs text-gray-500 line-through">{formatINR(s.price)}</div>}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1 text-xs font-bold text-[#E65C00] group-hover:gap-2 transition-all">
                          <ShoppingCart className="w-3.5 h-3.5" /> Order Now <ArrowRight className="w-3 h-3" />
                        </div>
                        <WhatsAppInquireButton itemTitle={s.title} kind="spare" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </Section>
    </>
  );
}
